import { useEffect, useState } from 'react';
import api from './api';

/**
 * 접속 계정 성별에 맞는 전신 3D 모델 선택.
 *
 * 디지털트윈 화면(pages/DigitalTwin)과 심리 홈 화면(modules/mental)이 같은 모델
 * 두 개를 공유하므로 여기에 둔다. 심리 모듈에서 DigitalTwinShared 를 import 하면
 * 그 파일이 끌어오는 chart.js · mui-x-charts · Bottom 까지 같이 딸려온다.
 */

// ── 모델 경로 ─────────────────────────────────────────────────

/**
 * public 에 두고 경로로 받는다. import 하지 않는 이유:
 * import 하면 webpack 이 static/media 로 한 부 더 방출해서, public 의 원본과
 * 합쳐 같은 13MB 파일이 배포물에 두 번 들어간다(실제로 그런 상태였다).
 * 경로로 받으면 두 화면이 같은 URL 을 쓰므로 브라우저 캐시도 공유된다.
 */
export const WOMAN_MODEL_PATH = '/model/woman.glb';
export const MAN_MODEL_PATH = '/model/man.glb';

/** 성별 미확정(null)은 여성 모델 — 기존 동작을 유지한다. */
export const bodyModelPath = (male: boolean | null) =>
  male === true ? MAN_MODEL_PATH : WOMAN_MODEL_PATH;

// ── 성별 판정 ─────────────────────────────────────────────────

/**
 * 성별 판정. 남성이면 true, 여성이면 false, 판정 근거가 없으면 null.
 *
 * 같은 값을 API 마다 다른 표기로 준다 — `permission/getProfile` · `permission/isAuth`
 * 는 `sex`='M'/'F' 와 `gender`='1'/'0' 을, `AP_009_002` 는 `user.sex` boolean 을
 * 준다. 어느 표기든 받도록 해서 호출부가 출처를 신경 쓰지 않게 한다.
 *
 * 매핑 근거(실측): 여성 계정에서 `getProfile` sex='F' gender='0',
 * `AP_009_002` user.sex=false 가 일치하고, 가입 화면(Join/UserJoinPrivate)이
 * 남성 토글을 `sex: alignment !== '0'` 로 true 로 보낸다.
 *
 * '모르는 상태'를 false(여성)로 뭉개지 않는 이유: 모델이 13MB 라 잘못 고르면
 * 성별이 확정된 뒤 한 번 더 받아야 한다. 호출부가 null 동안 로드를 미룰 수 있어야 한다.
 */
export const isMaleSex = (sex: unknown): boolean | null => {
  if (sex === true || sex === 1 || sex === '1' || sex === 'M' || sex === 'm') return true;
  if (sex === false || sex === 0 || sex === '0' || sex === 'F' || sex === 'f') return false;
  return null;
};

// ── getProfile 캐시 ───────────────────────────────────────────

/**
 * 이 값이 어느 계정 것인지. 계정이 바뀌면 캐시를 버린다 —
 * 로그아웃·계정 전환이 soft navigation 이라 모듈 스코프가 유지되기 때문에,
 * 키를 비교하지 않으면 이전 계정의 성별로 모델이 뜬다.
 */
const accountKey = () => window.localStorage.getItem('userCusID') ?? '';

let cached: Promise<boolean | null> | null = null;
let cachedKey: string | null = null;

/**
 * `permission/getProfile` 로 성별을 읽는다.
 *
 * 이 프로젝트에는 이미 getProfile 을 각자 호출하는 화면이 8곳 있는데 모두 캐시가
 * 없다. 여기서는 모듈 캐시를 둬서 두 3D 화면이 요청을 한 번만 보낸다.
 * (utils/cachedResource 를 쓰지 않는 이유: 여기서는 reload·사용자노출오류 같은
 *  그쪽 기능이 필요하지 않다. 필요해지면 그걸로 갈아타면 된다.)
 */
export const fetchUserSex = (): Promise<boolean | null> => {
  const key = accountKey();
  if (cached === null || cachedKey !== key) {
    cachedKey = key;
    cached = api
      .get('permission/getProfile')
      .then((res: any) => {
        const d = res?.data ?? {};
        // sex('M'/'F') 를 먼저 보고, 없으면 gender('1'/'0') 로 판정한다.
        const bySex = isMaleSex(d.sex);
        return bySex !== null ? bySex : isMaleSex(d.gender);
      })
      .catch((err) => {
        // 실패는 캐시하지 않는다 — 다시 시도할 수 있어야 한다.
        cached = null;
        cachedKey = null;
        throw err;
      });
  }
  return cached;
};

export type UserSex = {
  /** 남성 true / 여성 false / 판정 불가 null */
  male: boolean | null;
  /** 조회가 끝났는지. false 면 아직 모델을 고르지 말 것. */
  resolved: boolean;
};

/**
 * 성별 조회 훅.
 *
 * 조회 실패·비로그인도 `resolved: true` + `male: null` 로 끝낸다 —
 * 호출부가 영원히 기다리며 모델을 못 띄우는 상황을 만들지 않기 위함이다.
 */
export const useUserSex = (): UserSex => {
  const key = accountKey();
  const [state, setState] = useState<UserSex>({ male: null, resolved: false });

  useEffect(() => {
    if (key === '') {
      // 비로그인 — 조회할 것이 없다.
      setState({ male: null, resolved: true });
      return;
    }
    let alive = true;
    setState({ male: null, resolved: false });
    fetchUserSex()
      .then((male) => {
        if (alive) setState({ male, resolved: true });
      })
      .catch((err) => {
        console.log(err);
        if (alive) setState({ male: null, resolved: true });
      });
    return () => {
      alive = false;
    };
  }, [key]);

  return state;
};
